import type { CheckStatus, HealthCheck, HealthRelationship, HealthRun, HealthSubject, HealthTable, Layer } from '@/frontend/features/Fde/dataHealth/types'

// The page reads the Forge run as it is. Older data-health/2 runs (before subjects and the access
// list) are read the same way: their table_readable checks become the access list and their tables
// are grouped by entity.

export const STATUS_ORDER: Record<CheckStatus, number> = { fail: 0, warn: 1, ok: 2 }
export const MAIN_LAYERS: Layer[] = ['core', 'mart']
export const TECHNICAL_LAYERS: Layer[] = ['raw', 'stg', 'other']
const LAYER_ORDER: Record<string, number> = { raw: 0, stg: 1, other: 2, core: 3, mart: 4 }

export interface CheckGroup {
  metric: string
  status: CheckStatus
  checks: HealthCheck[]
  tables: number
  example: HealthCheck
}

export interface HealthModel {
  checks: HealthCheck[]
  counts: Record<CheckStatus, number>
  unreadable: string[]
  subjects: HealthSubject[]
  byId: Map<string, HealthTable>
  checksByTable: Map<string, HealthCheck[]>
  relationships: HealthRelationship[]
  // dictionary labels shared by more than one table
  ambiguous: Set<string>
}

function isAccessCheck(check: HealthCheck) {
  return check.metric === 'table_readable' && check.error === 'not_readable'
}

export function layerRank(layer: string) {
  return LAYER_ORDER[layer] ?? 9
}

function worst(statuses: CheckStatus[]): CheckStatus | null {
  if (!statuses.length) return null
  return statuses.reduce((a, b) => (STATUS_ORDER[a] <= STATUS_ORDER[b] ? a : b))
}

// Subjects of an older run: one per entity, summed the way Forge sums them.
function deriveSubjects(tables: HealthTable[], checksByTable: Map<string, HealthCheck[]>, asOf?: string | null): HealthSubject[] {
  const groups = new Map<string, HealthTable[]>()
  for (const table of tables) {
    const id = table.subject ?? table.entity
    groups.set(id, [...(groups.get(id) ?? []), table])
  }
  return [...groups.entries()].map(([id, items]) => {
    const readable = items.filter((table) => table.readable && table.rows != null)
    const top = Math.max(-1, ...readable.map((table) => layerRank(table.layer)))
    const final = readable
      .filter((table) => layerRank(table.layer) === top)
      .sort((a, b) => Number(!!b.daily) - Number(!!a.daily) || (b.rows ?? 0) - (a.rows ?? 0))[0]
    const daily = readable.filter((table) => table.daily && table.last_date)
    const dailyTop = Math.max(-1, ...daily.map((table) => layerRank(table.layer)))
    const last = daily.filter((table) => layerRank(table.layer) === dailyTop).map((table) => table.last_date as string).sort().pop() ?? null
    const checks = items.flatMap((table) => checksByTable.get(table.id) ?? [])
    const counts = { ok: 0, warn: 0, fail: 0 }
    checks.forEach((check) => { counts[check.status] += 1 })
    const days = last && asOf ? Math.round((Date.parse(`${asOf}T00:00:00Z`) - Date.parse(`${last}T00:00:00Z`)) / 86_400_000) : null
    return {
      id, tables: items.map((table) => table.id),
      layers: [...new Set(items.map((table) => table.layer))].sort((a, b) => layerRank(a) - layerRank(b)),
      final_table: final?.id ?? null, final_layer: final?.layer ?? null, rows: final?.rows ?? null,
      last_date: last, days_since_last: days, status: worst(checks.map((check) => check.status)), checks: counts,
    }
  })
}

export function buildModel(run: HealthRun): HealthModel {
  const tables = run.tables ?? []
  const byId = new Map(tables.map((table) => [table.id, table]))
  const all = run.checks ?? []
  const checks = all.filter((check) => !isAccessCheck(check))
  const unreadable = run.access?.unreadable_tables
    ?? [...new Set([...all.filter(isAccessCheck).map((check) => check.table), ...tables.filter((table) => !table.readable).map((table) => table.id)])]
  const counts = { ok: 0, warn: 0, fail: 0 }
  checks.forEach((check) => { counts[check.status] += 1 })
  const checksByTable = new Map<string, HealthCheck[]>()
  for (const check of checks) checksByTable.set(check.table, [...(checksByTable.get(check.table) ?? []), check])
  const subjects = (run.subjects?.length ? run.subjects : deriveSubjects(tables, checksByTable, run.as_of))
    .slice()
    .sort((a, b) => (STATUS_ORDER[a.status ?? 'ok'] - STATUS_ORDER[b.status ?? 'ok']) || (b.rows ?? 0) - (a.rows ?? 0))
  const labelCount = new Map<string, number>()
  for (const table of tables) {
    const label = table.label?.trim()
    if (label) labelCount.set(label, (labelCount.get(label) ?? 0) + 1)
  }
  const ambiguous = new Set([...labelCount.entries()].filter(([, count]) => count > 1).map(([label]) => label))
  return { checks, counts, unreadable, subjects, byId, checksByTable, relationships: run.relationships ?? [], ambiguous }
}

// Checks of one status grouped by metric: the group with the most tables first.
export function groupChecks(checks: HealthCheck[], status: CheckStatus): CheckGroup[] {
  const groups = new Map<string, HealthCheck[]>()
  for (const check of checks.filter((item) => item.status === status)) {
    groups.set(check.metric, [...(groups.get(check.metric) ?? []), check])
  }
  return [...groups.entries()]
    .map(([metric, items]) => {
      // furthest from its limit first
      const sorted = [...items].sort((a, b) => distance(b) - distance(a))
      return { metric, status, checks: sorted, tables: new Set(items.map((check) => check.table)).size, example: sorted[0] }
    })
    .sort((a, b) => b.tables - a.tables || a.metric.localeCompare(b.metric))
}

function distance(check: HealthCheck) {
  if (check.value == null) return Number.MAX_SAFE_INTEGER
  return check.op === '<=' ? check.value - check.threshold : check.threshold - check.value
}

export function sortChecks(checks: HealthCheck[]) {
  return [...checks].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.metric.localeCompare(b.metric))
}
